import { useState, useEffect, useCallback } from 'react';
import { Hanko } from '@teamhanko/hanko-frontend-sdk';
import { IS_NETCONSOLE } from '../../utils/brand';
import { createHankoClient } from '../../utils/hanko';

interface PasskeyModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Passkey {
  id: string;
  name?: string;
  public_key: string;
  attestation_type: string;
  aaguid: string;
  created_at: string;
  last_used_at?: string;
  transports: string;
}

interface ProfileState {
  name: string;
  payload?: {
    user?: {
      passkeys?: Passkey[];
    };
  };
  actions?: {
    webauthn_credential_create?: {
      run: () => Promise<ProfileState>;
    };
    webauthn_credential_delete?: {
      run: (inputs: { passkey_id: string }) => Promise<ProfileState>;
    };
  };
}

export default function PasskeyModal({ isOpen, onClose }: PasskeyModalProps) {
  const [hanko] = useState<Hanko | null>(() => createHankoClient());
  const [profileState, setProfileState] = useState<ProfileState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Reset for the next open (event-driven so the open effect never needs a
  // synchronous setState; also stops loadProfile's old setError(null) prefix
  // from erasing add/delete failure messages during the post-action reload).
  const handleClose = useCallback(() => {
    setProfileState(null);
    setError(null);
    setLoading(true);
    onClose();
  }, [onClose]);

  const loadProfile = useCallback(async () => {
    if (!hanko) return;

    try {
      // Create a profile flow state to get user info and actions
      const state = (await hanko.createState('profile', {
        loadFromCache: false,
      })) as unknown as ProfileState;
      // An unauthenticated Hanko session lands the flow in the 'error' state,
      // which exposes no passkey actions. Surface this as a re-auth prompt
      // rather than a generic failure.
      if (state.name === 'error') {
        setError('Your session has expired. Please sign out and sign in again to manage passkeys.');
        setProfileState(null);
        return;
      }
      setProfileState(state);
    } catch (err) {
      console.error('Failed to load profile:', err);
      setError('Failed to load passkeys');
    } finally {
      setLoading(false);
    }
  }, [hanko]);

  useEffect(() => {
    if (isOpen && hanko) {
      // Microtask defer keeps every setState in loadProfile off the effect's
      // synchronous path (react-hooks/set-state-in-effect).
      void Promise.resolve().then(loadProfile);
    }
  }, [isOpen, hanko, loadProfile]);

  const passkeys = profileState?.payload?.user?.passkeys || [];

  const addPasskey = async () => {
    if (!profileState?.actions?.webauthn_credential_create) {
      setError('Passkey creation not available');
      return;
    }

    setAdding(true);
    setError(null);

    try {
      // Start passkey creation flow
      let state = await profileState.actions.webauthn_credential_create.run();
      console.log('After webauthn_credential_create, state:', state.name, state);

      // Handle the attestation verification state if we're in that flow
      while (state.name !== 'profile_init' && state.name !== 'error') {
        const actions = state.actions as Record<string, { run: (inputs?: unknown) => Promise<ProfileState> } | undefined>;
        console.log('State loop - current state:', state.name, 'actions:', Object.keys(actions || {}));

        if (actions?.webauthn_verify_attestation_response) {
          state = await actions.webauthn_verify_attestation_response.run();
          console.log('After webauthn_verify_attestation_response, state:', state.name, state);
        } else if (actions?.back) {
          // Something went wrong, go back
          state = await actions.back.run();
          break;
        } else {
          console.log('No matching action found, breaking out of loop');
          break;
        }
      }

      console.log('Final state:', state.name, 'passkeys:', state.payload?.user?.passkeys);

      // Always reload profile after passkey creation to get fresh list
      // The state returned from creation flow may not include updated passkeys
      await loadProfile();
    } catch (err: unknown) {
      console.error('Failed to add passkey:', err);
      if (err instanceof Error && err.name === 'NotAllowedError') {
        setError('Passkey registration was cancelled');
      } else {
        setError('Failed to add passkey');
      }
      // Reload profile to get fresh state
      await loadProfile();
    } finally {
      setAdding(false);
    }
  };

  const deletePasskey = async (id: string) => {
    if (!profileState?.actions?.webauthn_credential_delete) {
      setError('Passkey deletion not available');
      return;
    }

    if (!confirm('Are you sure you want to remove this passkey?')) {
      return;
    }

    setDeletingId(id);
    setError(null);

    try {
      const state = await profileState.actions.webauthn_credential_delete.run({
        passkey_id: id,
      });
      setProfileState(state);
    } catch (err) {
      console.error('Failed to delete passkey:', err);
      setError('Failed to remove passkey');
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getPasskeyDisplayName = (passkey: Passkey) => {
    if (passkey.name) return passkey.name;

    // Try to derive a name from transports
    const transports = passkey.transports || '';
    if (transports.includes('internal')) return 'Built-in Passkey';
    if (transports.includes('usb')) return 'Security Key (USB)';
    if (transports.includes('nfc')) return 'Security Key (NFC)';
    if (transports.includes('ble')) return 'Security Key (Bluetooth)';

    return 'Passkey';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl max-w-md w-full p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Passkeys
            </h2>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          {/* Description */}
          <p className="text-gray-600 text-sm mb-4">
            Passkeys allow you to sign in securely without a password using your
            fingerprint, face, or device PIN.
          </p>

          {/* NetConsole notice */}
          {IS_NETCONSOLE && (
            <div className="mb-4 p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg text-sm">
              Passkeys are bound to cloudunifi.com and cannot be created or used on netconsole.cloud.
              To manage passkeys, please visit{' '}
              <a
                href="https://portal.cloudunifi.com/settings"
                className="font-medium underline hover:text-amber-900"
              >
                portal.cloudunifi.com
              </a>.
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          {/* Loading */}
          {loading ? (
            <div className="py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-gray-300 border-t-blue-600 mb-2"></div>
              <p className="text-gray-500">Loading passkeys...</p>
            </div>
          ) : (
            <>
              {/* Passkey List */}
              {passkeys.length > 0 ? (
                <div className="space-y-3 mb-6">
                  {passkeys.map((passkey) => (
                    <div
                      key={passkey.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <svg
                            className="w-5 h-5 text-gray-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                            />
                          </svg>
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">
                              {getPasskeyDisplayName(passkey)}
                            </span>
                            <span className="text-xs px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded">
                              cloudunifi.com
                            </span>
                          </div>
                          <div className="text-sm text-gray-500">
                            Added {formatDate(passkey.created_at)}
                            {passkey.last_used_at && (
                              <> · Last used {formatDate(passkey.last_used_at)}</>
                            )}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => deletePasskey(passkey.id)}
                        disabled={deletingId === passkey.id}
                        className="text-red-600 hover:text-red-800 text-sm font-medium disabled:opacity-50"
                      >
                        {deletingId === passkey.id ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-6 text-center mb-6">
                  <svg
                    className="w-12 h-12 text-gray-300 mx-auto mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M12 11c0 3.517-1.009 6.799-2.753 9.571m-3.44-2.04l.054-.09A13.916 13.916 0 008 11a4 4 0 118 0c0 1.017-.07 2.019-.203 3m-2.118 6.844A21.88 21.88 0 0015.171 17m3.839 1.132c.645-2.266.99-4.659.99-7.132A8 8 0 008 4.07M3 15.364c.64-1.319 1-2.8 1-4.364 0-1.457.39-2.823 1.07-4"
                    />
                  </svg>
                  <p className="text-gray-500">No passkeys registered yet</p>
                </div>
              )}

              {/* Add Passkey Button - hidden on netconsole.cloud since passkeys are bound to cloudunifi.com */}
              {!IS_NETCONSOLE && (
                <button
                  onClick={addPasskey}
                  disabled={adding || !profileState?.actions?.webauthn_credential_create}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors flex items-center justify-center gap-2"
                >
                  {adding ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Registering...
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Add Passkey
                    </>
                  )}
                </button>
              )}
            </>
          )}

          {/* Done Button */}
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
